import { Box, Button, Card, CardHeader, Divider, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import { useEditContext } from "ra-core";
import { ValidationFrequency, ValidationScheduleResponseDto } from "~/gen/api/backup";
import { RecPatch } from "~/dataProvider/tools";
import BackupSchedulePicker from "./BackupSchedulePicker";
import { useSelector } from "react-redux";
import { getUserProfile } from "~/reducers/userProfile";
import { useDatabaseInfoContext } from "./DatabaseInfoContext";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { MANAGE_BACKUP_JOBS } from "~/constants";


const weekdayName: Record<string, string> = {
  'SUN': 'Sunday',
  'MON': 'Monday',
  'TUE': 'Tuesday',
  'WED': 'Wednesday',
  'THU': 'Thurday',
  'FRI': 'Friday',
  'SAT': 'Saturday',
}

const BackupValidationScheduleCard: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_BACKUP_JOBS])
  const { databaseInfo, deploymentInfo } = useDatabaseInfoContext()
  const { record } = useEditContext<ValidationScheduleResponseDto & RecPatch>({ resource: 'validation-schedule', basePath: '' })
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleAddInit = (): void => {
    setIsExpanded(true);
  };

  const handleClosePicker = useCallback((): void => {
    setIsExpanded(false);
  }, [setIsExpanded]);

  const detailContent =
    record?.frequency === ValidationFrequency.Hourly ?
    `Every ${record.interval} hour(s)`
    : record?.frequency === ValidationFrequency.Daily ?
    `Every ${record.interval} day(s)`
    : record?.frequency === ValidationFrequency.Weekly ?
    `Every ${record.interval} week(s) on ${
      ((ls) => {
        if (!ls) return '';

        return ls.length >= 3 
          ? `${ls.splice(0, ls.length - 1).join(', ')} and ${ls[ls.length - 1]}`
          : ls.join(', ')
      })(record.daysOfWeek?.split(',').map(d => d && weekdayName[d]))}`
    : record?.frequency === ValidationFrequency.Monthly ?
    `Every ${record.interval} month(s) on day ${record.dayOfMonth}`
    : null

  const user = useSelector(getUserProfile)

  const offsetToUTC = 
    record?.timeZoneOffset !== undefined ? Number.parseInt(record.timeZoneOffset || '0') 
    : user?.timeZoneOffset !== undefined ? (user.timeZoneOffset || 0) 
    : new Date().getTimezoneOffset()
  const offsetHr = Math.abs(Math.floor(offsetToUTC / 60)).toString().padStart(2, '0')
  const offsetMn = Math.abs(offsetToUTC % 60).toString().padStart(2, '0')
  const timezoneTipText = `UTC${offsetToUTC == Math.abs(offsetToUTC) ? '+' : '-'}${offsetHr}:${offsetMn}`
  const startTimeText = useMemo(() => {
    if (!record) return ''

    return `${(record.startHour || 0).toString().padStart(2, '0')} : ${(record.startMinute || 0).toString().padStart(2, '0')}`
  }, [record, offsetToUTC])

  return (
    <Card>
      <CardHeader title="Validation Schedule" id="validation-schedule-header" />
      <Divider />
      {hasWritePermit && isExpanded
      ? <BackupSchedulePicker onClose={handleClosePicker} />
      :
        <>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                  >
                    Frequency
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                  >
                    { record?.frequency === ValidationFrequency.Hourly ? 'Hourly'
                    : record?.frequency === ValidationFrequency.Daily ? 'Daily'
                    : record?.frequency === ValidationFrequency.Weekly ? 'Weekly'
                    : record?.frequency === ValidationFrequency.Monthly ? 'Monthly'
                    : null}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                  >
                    Details
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                  >
                    {detailContent}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                  >
                    Start Time
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                  >
                    {startTimeText}
                    <Typography
                      variant="caption"
                      sx={{ pl: 2 }}
                    >
                      ({timezoneTipText})
                    </Typography>
                  </Typography>
                </TableCell>
              </TableRow>
              {/* <TableRow>
                <TableCell>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                  >
                    Estimated Billing Amount ($/Month)
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                  >
                    $60 - test
                  </Typography>
                </TableCell>
              </TableRow> */}
            </TableBody>
          </Table>
          {hasWritePermit && databaseInfo && (!databaseInfo.isManaged || (deploymentInfo && !deploymentInfo.isDeleted)) &&
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                m: 1
              }}
            >
              <Button
                aria-describedby="validation-schedule-header"
                color="primary"
                onClick={handleAddInit}
                variant="text"
              >
                Update
              </Button>
            </Box>
          }
        </>
      }
    </Card>
  )
}

export default BackupValidationScheduleCard;
